import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/kit";
import { ConsentDocument } from "@/components/ConsentDocument";
import { STATUS_TONE } from "@/pages/MyConsent";
import { TERM_LABELS, printConsent, fmtLong } from "@/lib/consentTemplate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SignaturePad } from "@/components/SignaturePad";
import { useAuth } from "@/context/AuthContext";
import { Users, CheckCircle2, Clock, HelpCircle, LogOut, Printer, Send, FileText, Pencil, RotateCcw, AlertTriangle, PenLine } from "lucide-react";

const err = (e) => toast.error(e?.response?.data?.detail || "Something went wrong");

function SettingsCard({ s, refresh }) {
  const [active, setActive] = useState(s.active);
  const [end, setEnd] = useState(s.end_date);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    try { await api.put("/suspension/settings", { active, end_date: end }); toast.success("Suspension settings saved"); refresh(); } catch (e) { err(e); }
  };
  const notify = async () => {
    setBusy(true);
    try { const r = (await api.post("/suspension/notify-all")).data; toast.success(`Notice sent to ${r.recipients} people (${r.emailed} emails)`); refresh(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <SectionCard title="Suspension status" testId="suspension-settings">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
        <label className="flex items-center gap-3 text-sm font-medium text-navy">
          <Switch checked={active} onCheckedChange={setActive} data-testid="suspension-active-switch" />
          {active ? "Suspension active" : "Operations resumed"}
        </label>
        <div><div className="text-xs text-slate-500">Effective</div><div className="text-sm font-semibold text-navy">{fmtLong(s.effective_date)}</div></div>
        <div><div className="text-xs text-slate-500">Ends on</div>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44" data-testid="suspension-end-date" /></div>
        <Button onClick={save} className="bg-navy text-gold hover:bg-navy/90" data-testid="suspension-save-btn">Save</Button>
        <Button variant="outline" disabled={busy} onClick={notify} data-testid="suspension-notify-btn"><Send className="mr-2 h-4 w-4" />Notify all employees</Button>
      </div>
      <p className="mt-3 text-xs text-slate-500" data-testid="suspension-notified">
        {s.notified_at ? `Notice last sent ${fmtLong(s.notified_at)} (${s.notified_count} emails).` : "Employees have not been emailed the notice yet."}
      </p>
    </SectionCard>
  );
}

function TermsDialog({ row, onClose, refresh }) {
  const [t, setT] = useState({});
  useEffect(() => { setT(row?.terms || {}); }, [row]);
  const save = async () => {
    try { await api.put(`/suspension/terms/${row.employee_id}`, t); toast.success("Terms saved"); refresh(); onClose(); } catch (e) { err(e); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="terms-dialog">
        <DialogHeader><DialogTitle>Approved terms — {row?.employee?.name}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          {Object.entries(TERM_LABELS).map(([k, label]) => (
            <label key={k} className="text-xs font-medium text-slate-600">{label}
              <Input type={k === "effective_date" ? "date" : "text"} value={t[k] || ""} onChange={(e) => setT({ ...t, [k]: e.target.value })} className="mt-1" data-testid={`terms-${k}`} />
            </label>
          ))}
        </div>
        <DialogFooter><Button onClick={save} className="bg-navy text-gold hover:bg-navy/90" data-testid="terms-save-btn">Save terms</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CountersignDialog({ row, onClose, refresh }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  useEffect(() => { setName(user?.name || ""); setSig(""); }, [row, user]);
  const save = async () => {
    try { await api.post(`/suspension/${row.employee_id}/countersign`, { signature_image: sig, name }); toast.success("Counter-signed"); refresh(); onClose(); } catch (e) { err(e); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="countersign-dialog">
        <DialogHeader><DialogTitle>Counter-sign — {row?.employee?.name}</DialogTitle></DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="countersign-name" />
        {row && <SignaturePad onChange={setSig} testId="countersign-pad" />}
        <DialogFooter><Button disabled={!sig || name.trim().length < 3} onClick={save} className="bg-navy text-gold hover:bg-navy/90" data-testid="countersign-save-btn">Counter-sign</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocDialog({ row, onClose }) {
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="consent-view-dialog">
        {row && <ConsentDocument consent={row} />}
        <DialogFooter><Button variant="outline" onClick={() => printConsent(row)} data-testid="consent-view-print"><Printer className="mr-2 h-4 w-4" />Print</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConsentRow({ r, onTerms, onView, onSign, refresh }) {
  const reset = async () => {
    const msg = r.status === "Wishes to Exit" ? "Reinstate this employee's access and reset their decision to Pending?" : "Reset this decision to Pending?";
    if (!window.confirm(msg)) return;
    try { await api.post(`/suspension/${r.employee_id}/reset`); toast.success("Reset to Pending"); refresh(); } catch (e) { err(e); }
  };
  const id = r.employee_id;
  return (
    <TableRow data-testid={`consent-row-${id}`}>
      <TableCell><div className="font-medium text-navy">{r.employee.name}</div><div className="text-xs text-slate-500">{id} · {r.employee.designation || r.employee.department}</div></TableCell>
      <TableCell><Badge variant="outline" className={STATUS_TONE[r.status]} data-testid={`consent-status-${id}`}>{r.status}</Badge></TableCell>
      <TableCell className="text-xs">{r.missing_terms.length ? <span className="flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />{r.missing_terms.length} missing</span> : <span className="text-emerald-700">Complete</span>}</TableCell>
      <TableCell className="text-xs text-slate-600">{r.signed_at ? `${r.signature_name} · ${fmtLong(r.signed_at)}${r.countersigned_at ? " · counter-signed" : " · awaiting counter-sign"}` : r.exited_at ? `Exited ${fmtLong(r.exited_at)}` : "—"}</TableCell>
      <TableCell className="max-w-[220px] text-xs text-slate-600">{r.note || "—"}</TableCell>
      <TableCell className="whitespace-nowrap no-print">
        <Button size="icon" variant="ghost" onClick={() => onView(r)} data-testid={`consent-view-${id}`}><FileText className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => onTerms(r)} data-testid={`consent-terms-${id}`}><Pencil className="h-4 w-4" /></Button>
        {r.status === "Will Continue" && !r.countersigned_at && <Button size="icon" variant="ghost" onClick={() => onSign(r)} data-testid={`consent-countersign-${id}`}><PenLine className="h-4 w-4 text-gold-dark" /></Button>}
        {r.status !== "Pending" && <Button size="icon" variant="ghost" onClick={reset} data-testid={`consent-reset-${id}`}><RotateCcw className="h-4 w-4" /></Button>}
      </TableCell>
    </TableRow>
  );
}

export default function Suspension() {
  const qc = useQueryClient();
  const [terms, setTerms] = useState(null);
  const [view, setView] = useState(null);
  const [csign, setCsign] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["suspension-report"], queryFn: async () => (await api.get("/suspension/report")).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["suspension-report"] }); qc.invalidateQueries({ queryKey: ["suspension-me"] }); };
  if (isLoading) return <Skeleton className="h-96 w-full" />;
  const c = data.counts;
  return (
    <div className="space-y-6" data-testid="suspension-page">
      <PageHeader title="Suspension & Consent" subtitle="Data entry suspension, employee decisions and reallocation terms">
        <Button variant="outline" onClick={() => window.print()} data-testid="suspension-print-report"><Printer className="mr-2 h-4 w-4" />Print report</Button>
      </PageHeader>
      <SettingsCard key={`${data.suspension.active}-${data.suspension.end_date}`} s={data.suspension} refresh={refresh} />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiCard label="Employees" value={data.total} icon={Users} testId="kpi-total" />
        <KpiCard label="Will Continue" value={c["Will Continue"]} icon={CheckCircle2} testId="kpi-continue" />
        <KpiCard label="Pending" value={c.Pending} icon={Clock} testId="kpi-pending" />
        <KpiCard label="Clarification" value={c["Clarification Requested"]} icon={HelpCircle} testId="kpi-clarify" />
        <KpiCard label="Wishes to Exit" value={c["Wishes to Exit"]} icon={LogOut} testId="kpi-exit" />
      </div>
      {data.missing_terms_count > 0 && (
        <p className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="missing-terms-alert">
          <AlertTriangle className="h-4 w-4" />{data.missing_terms_count} employee(s) have terms awaiting management approval (hour cap, salary, manager, effective date). These show as “To be confirmed” in their document.
        </p>
      )}
      <SectionCard title="Employee decisions">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Status</TableHead><TableHead>Terms</TableHead><TableHead>Signed / Exited</TableHead><TableHead>Note</TableHead><TableHead className="no-print">Actions</TableHead></TableRow></TableHeader>
            <TableBody>{data.rows.map((r) => <ConsentRow key={r.employee_id} r={r} onTerms={setTerms} onView={setView} onSign={setCsign} refresh={refresh} />)}</TableBody>
          </Table>
        </div>
      </SectionCard>
      <TermsDialog row={terms} onClose={() => setTerms(null)} refresh={refresh} />
      <DocDialog row={view} onClose={() => setView(null)} />
      <CountersignDialog row={csign} onClose={() => setCsign(null)} refresh={refresh} />
    </div>
  );
}
